import { Container, Graphics, Sprite, type Texture } from "pixi.js";
import type { FeatureStateStore } from "@slot/game-shell";

/**
 * Geometry lifted from `GridContainer.prefab` (y-up values flipped to Pixi y-down).
 * GridArea is the grid's layout root; the mask + GridRoot sit 16.57 px below its centre.
 */
const GRID_CONTAINER_GEOMETRY = {
  container: { width: 1122.2, height: 828.3 },
  gridArea: { width: 1032.63, height: 695.756, y: 24.5 },
  frame: { width: 1300, height: 1180 },//  frame: { width: 1217.293, height: 834.86 },
  mask: { width: 1234.44, height: 700.98, y: 16.57 },
} as const;

/** The grid's layout area (the box the symbols fit into). */
export const GRID_AREA_SIZE = { width: GRID_CONTAINER_GEOMETRY.gridArea.width, height: GRID_CONTAINER_GEOMETRY.gridArea.height } as const;

/**
 * The `GridContainer` prefab as a container: base/feature frame sprites (switched by the feature
 * state, `GridFrameModeBackground`), FX underlays, the clipping mask with the cell underlay and the symbol
 * `gridRoot`, and the grid overlay. A slam tap is the shell's (its full-screen surface over the scene);
 * until 2026-09-26 the grid area was also made hit-testable "for slam" with nothing listening (review finding 74).
 */
export class GridContainer extends Container {
  public readonly gridArea: Container;
  public readonly gridRoot: Container;
  /** Under the symbols, inside the clip, in `gridRoot`'s own space: a cell's local position places a node here too. */
  public readonly cellUnderlay: Container;
  public readonly gridOverlay: Container;
  public readonly fxUnderlays: Container;
  private readonly baseFrame: Sprite | null;
  private readonly featureFrame: Sprite | null;
  private unsubscribe: (() => void) | null = null;

  public constructor(input: { baseFrame: Texture | null; featureFrame: Texture | null }) {
    super({ label: "GridContainer" });
    const g = GRID_CONTAINER_GEOMETRY;

    this.gridArea = new Container({ label: "GridArea" });
    this.gridArea.position.set(0, g.gridArea.y);
    this.addChild(this.gridArea);

    const frameLayer = new Container({ label: "GridFrameLayer" });
    this.baseFrame = input.baseFrame ? GridContainer.frameSprite(input.baseFrame, "BaseFrame") : null;
    this.featureFrame = input.featureFrame ? GridContainer.frameSprite(input.featureFrame, "FeatureFrame") : null;
    if (this.baseFrame) frameLayer.addChild(this.baseFrame);
    if (this.featureFrame) frameLayer.addChild(this.featureFrame);
    if (this.featureFrame) this.featureFrame.visible = false;

    this.fxUnderlays = new Container({ label: "FxUnderlays" });

    const mask = new Container({ label: "GridContainerMask" });
    mask.position.set(0, g.mask.y);
    const shape = new Graphics().rect(-g.mask.width / 2, -g.mask.height / 2, g.mask.width, g.mask.height).fill(0xffffff);
    shape.label = "MaskShape";
    mask.addChild(shape);
    mask.mask = shape;
    this.cellUnderlay = new Container({ label: "CellUnderlay" });
    mask.addChild(this.cellUnderlay);
    this.gridRoot = new Container({ label: "GridRoot" });
    mask.addChild(this.gridRoot);

    this.gridOverlay = new Container({ label: "GridOverlay" });
    this.gridArea.addChild(frameLayer, this.fxUnderlays, mask, this.gridOverlay);
  }

  private static frameSprite(texture: Texture, label: string): Sprite {
    const sprite = new Sprite({ texture, anchor: 0.5, label });
    sprite.width = GRID_CONTAINER_GEOMETRY.frame.width;
    sprite.height = GRID_CONTAINER_GEOMETRY.frame.height;
    return sprite;
  }

  /** Base frame in the base game, feature frame during a feature. */
  public bindFeatureState(store: FeatureStateStore): void {
    this.unsubscribe?.();
    const render = (): void => {
      const featureActive = store.isFeatureActive();
      if (this.baseFrame) this.baseFrame.visible = !featureActive || !this.featureFrame;
      if (this.featureFrame) this.featureFrame.visible = featureActive;
    };
    this.unsubscribe = store.subscribe(render);
    render();
  }

  public dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.destroy({ children: true });
  }
}
